import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { Loader2, BadgeCheck, ShieldCheck, AlertTriangle, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { useTruthState } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PassportShareView — instructor-facing. Opens a secure learner passport
 * share by token. Access is audited server-side. Consent scoping is applied
 * by the backend (any non-consented data is absent from the response).
 */
export default function PassportShareView() {
  const { token } = useParams();
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const truth = useTruthState();

  const load = useCallback(async () => {
    try {
      await truth.run(
        async () => {
          const r = await axios.get(`${API}/api/passport/share/${token}`, { withCredentials: true });
          setData(r.data);
          setErr("");
        },
        { failedMessage: "Unable to load share." },
      );
    } catch (e) {
      setErr(e?.response?.data?.detail || "Unable to load share");
    }
    setLoading(false);
  }, [token, truth]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  if (loading) return (
    <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );

  if (err) return (
    <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120] p-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-3 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <p className="font-bold text-slate-900 dark:text-white">Share unavailable</p>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300" data-testid="share-view-error">{err}</p>
        <div className="mt-3">
          <TruthReceipt state={truth.state} message={truth.message} onRetry={load} onDismiss={() => truth.setState(null)} />
        </div>
        <Button onClick={() => goBack()} variant="outline" className="mt-4 w-full rounded-xl">Back</Button>
      </div>
    </div>
  );

  const p = data?.passport || {};
  const s = data?.share || {};

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="share-view-back-btn" onClick={() => goBack()} className="text-slate-400 hover:text-slate-600">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <ShieldCheck className="w-5 h-5 text-blue-600" />
          <p className="font-extrabold text-slate-900 dark:text-white text-sm flex-1">Learner Passport</p>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-4 space-y-4">
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5" data-testid="share-view-card">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900">
              {s.tier_label}
            </span>
            <BadgeCheck className="w-4 h-4 text-emerald-500" />
          </div>
          <p className="text-lg font-extrabold text-slate-900 dark:text-white" data-testid="share-view-learner">{p.name}</p>
          <p className="text-[11px] text-slate-400">
            {s.target_area ? `${s.target_area} · ` : ""}{s.transmission || "any transmission"}
          </p>
          <p className="text-xs text-slate-500 mt-2">Level estimate: <span className="font-bold">{p.level_estimate?.label}</span></p>

          <div className="mt-5 space-y-2 text-sm">
            {p.engagement?.theory_progress && (
              <Row label="Theory progress" value={`${p.engagement.theory_progress.percent}%`} sub={`${p.engagement.theory_progress.completed}/${p.engagement.theory_progress.total}`} />
            )}
            {p.engagement?.confidence && (
              <Row label="Confidence check-ins" value={p.engagement.confidence.checks_completed} sub={p.engagement.confidence.average_score != null ? `avg ${p.engagement.confidence.average_score}%` : null} />
            )}
            {p.engagement?.self_assessment && (
              <Row label="Self-assessment" value={p.engagement.self_assessment.level_name || "—"} sub={p.engagement.self_assessment.strength || null} />
            )}
            <Row label="Completed lessons" value={p.lessons?.total_completed || 0} />
          </div>

          <p className="text-[11px] text-slate-400 mt-4 italic" data-testid="share-view-disclaimer">
            {p.level_estimate?.disclaimer}
          </p>
        </div>

        <div className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 p-4 text-[12px] text-amber-900 dark:text-amber-200 leading-relaxed">
          This view is audited. The learner sees every time you open this passport.
        </div>
      </main>
    </div>
  );
}

function Row({ label, value, sub }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="font-bold text-slate-900 dark:text-white text-sm">{label}</p>
        {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
      </div>
      <p className="text-sm font-extrabold text-slate-900 dark:text-white">{value}</p>
    </div>
  );
}
